// @flow
import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchSchedule, setScheduleType } from '../../slices/scheduleSlice';
import type { State } from '../../general/types';
import ScheduleHeader from '../../schedule/schedule-header/ScheduleHeader';
import CoachCard from '../coach-card/CoachCard';
import { UserTypesEnum } from '../../utilities/userTypesEnum';
import { Redirect, useHistory, useLocation } from 'react-router';
import './CoachSchedule.scss';
import { getScheduleTypeClass } from '../../utilities/getScheduleTypeStyles';
import { Alert, Button, message, Select } from 'antd';
import {
  CheckOutlined,
  QuestionCircleOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import axios from 'axios';

export default function CoachSchedule() {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const typeIdFromUrl = queryParams.get('typeId');

  React.useEffect(() => {
    if (typeIdFromUrl) {
      dispatch(setScheduleType(parseInt(typeIdFromUrl, 10)));
    }
  }, [typeIdFromUrl, dispatch]);

  const handleBackToCalendar = () => {
    history.push('/coach-dashboard/calendar');
  };

  const schedule = useSelector((state: State) => {
    return state.schedule;
  });
  const scheduleError = useSelector((state: State) => state.schedule.error);
  const programs = useSelector((state: State) => state.programs);
  const currentUser = useSelector((state: State) => state.auth.currentUser);

  // Get programs the coach is assigned to
  const coachPrograms = React.useMemo(() => {
    if (!currentUser || !currentUser.program) return [];
    const programIds = currentUser.program;
    return programs.list
      .map((id) => programs.byId[id])
      .filter((program) => program && programIds.includes(program.id));
  }, [currentUser, programs]);

  // Get the current program name for error messages
  // Prefer programName from Redux (set by backend error response), fallback to lookup
  const currentProgramName = React.useMemo(() => {
    if (schedule.programName) return schedule.programName;
    if (!schedule.program) return null;
    const found = coachPrograms.find((p) => p.id === schedule.program);
    return found?.name || programs.byId[schedule.program]?.name || null;
  }, [schedule.programName, schedule.program, coachPrograms, programs.byId]);

  const handleProgramChange = (programId: number) => {
    dispatch(fetchSchedule({ program: programId }));
  };

  // Schedule confirmation state
  const [confirmed, setConfirmed] = React.useState(false);
  const [confirmationLoading, setConfirmationLoading] = React.useState(false);
  const [hasSchedule, setHasSchedule] = React.useState(true);

  const redirect = useSelector((state: State) => {
    if (
      state.auth.currentUser &&
      state.auth.currentUser.userType !== UserTypesEnum.COACH &&
      state.auth.currentUser.userType !== UserTypesEnum.TRANSPORTER
    ) {
      return '/login';
    }

    return '';
  });

  // Fetch current confirmation status on component mount
  React.useEffect(() => {
    fetchConfirmationStatus();
  }, []);

  const fetchConfirmationStatus = async () => {
    try {
      const response = await axios.get(
        `${
          process.env.REACT_APP_SERVER_URL || ''
        }/schedules/coach_schedule_confirmation/`
      );
      setConfirmed(response.data.confirmed);
      setHasSchedule(true);
    } catch (error) {
      if (error.response?.status === 404) {
        // No schedule for today
        setHasSchedule(false);
      } else {
        message.error('Failed to load schedule confirmation status');
      }
    }
  };

  const toggleConfirmation = async () => {
    if (!hasSchedule) return;

    setConfirmationLoading(true);
    try {
      const response = await axios.post(
        `${
          process.env.REACT_APP_SERVER_URL || ''
        }/schedules/coach_schedule_confirmation/`,
        {
          confirmed: !confirmed,
        }
      );

      setConfirmed(response.data.confirmed);
      message.success(
        response.data.confirmed
          ? 'Schedule confirmed successfully!'
          : 'Schedule confirmation removed'
      );
    } catch (error) {
      message.error('Failed to update schedule confirmation');
    } finally {
      setConfirmationLoading(false);
    }
  };

  if (redirect) {
    return <Redirect to={redirect} />;
  }

  return (
    <div>
      {typeIdFromUrl && (
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={handleBackToCalendar}
          style={{ margin: '16px 16px 0' }}
        >
          Back to Calendar
        </Button>
      )}
      <div className="header">
        <ScheduleHeader />
        {hasSchedule && !scheduleError && (
          <div
            className="schedule-confirmation-container"
            style={{ textAlign: 'center', padding: '16px' }}
          >
            <Button
              type={confirmed ? 'primary' : 'default'}
              icon={confirmed ? <CheckOutlined /> : <QuestionCircleOutlined />}
              loading={confirmationLoading}
              onClick={toggleConfirmation}
              disabled={confirmed}
              size="large"
              style={{
                backgroundColor: confirmed ? '#52c41a' : '#fff',
                borderColor: confirmed ? '#52c41a' : '#d9d9d9',
                color: confirmed ? '#fff' : '#000',
              }}
            >
              {confirmed ? 'Schedule Confirmed' : "Confirm Today's Schedule"}
            </Button>
          </div>
        )}
      </div>
      <div
        style={getScheduleTypeClass(schedule)}
        className={'coach-card-start'}
      >
        <div className="center">
          {scheduleError ? (
            <div style={{ margin: '20px' }}>
              <Alert
                message="Schedule Not Available"
                description={
                  currentProgramName
                    ? `${scheduleError} (Program: ${currentProgramName})`
                    : scheduleError
                }
                type="warning"
                showIcon
                style={{ marginBottom: '20px' }}
              />
              {coachPrograms.length > 1 && (
                <div style={{ textAlign: 'center' }}>
                  <label style={{ display: 'block', marginBottom: '8px' }}>
                    Select a different program:
                  </label>
                  <Select
                    style={{ width: 250 }}
                    placeholder="Select a program"
                    onChange={handleProgramChange}
                    value={schedule.program || undefined}
                  >
                    {coachPrograms.map((program) => (
                      <Select.Option key={program.id} value={program.id}>
                        {program.name}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              )}
            </div>
          ) : (
            <CoachCard />
          )}
        </div>
      </div>
    </div>
  );
}
